import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, AlertCircle } from "lucide-react";

const BG = "https://images.unsplash.com/photo-1483366774565-c783b9f70e2c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4NzYyOTAwNnww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, googleLogin, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const googleButton = useRef(null);
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  useEffect(() => { if (user) navigate("/"); }, [user, navigate]);

  useEffect(() => {
    if (!googleClientId) return;
    let cancelled = false;
    const render = () => {
      if (cancelled || !window.google?.accounts?.id || !googleButton.current) return false;
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: async ({ credential }) => {
          setError(""); setLoading(true);
          try {
            await googleLogin(credential);
            navigate("/");
          } catch (err) {
            setError(apiError(err));
          } finally {
            setLoading(false);
          }
        },
      });
      window.google.accounts.id.renderButton(googleButton.current, {
        type: "standard", theme: "outline", size: "large", width: 384, text: "continue_with",
      });
      return true;
    };
    if (!render()) {
      const timer = window.setInterval(() => { if (render()) window.clearInterval(timer); }, 200);
      return () => { cancelled = true; window.clearInterval(timer); };
    }
    return () => { cancelled = true; };
  }, [googleClientId, googleLogin, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-8 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="mb-8">
            <div className="font-display font-black text-3xl tracking-tight text-slate-900">PLSC</div>
            <div className="text-slate-500 text-sm mt-1">Management Operating System</div>
          </div>
          <h1 className="text-xl font-semibold text-slate-800 mb-2">Run the business. Own the outcome.</h1>
          <p className="text-sm text-slate-500 mb-6">One system. Every branch. Moving forward.</p>
          {error && (
            <div className="mb-4 flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 rounded-sm px-3 py-2 text-sm" data-testid="login-error">
              <AlertCircle className="h-4 w-4" /> {error}
            </div>
          )}
          {googleClientId && (
            <div className="space-y-3">
              <div ref={googleButton} className="min-h-10" data-testid="google-login-button" />
              <p className="text-xs text-slate-500 text-center">Only pre-approved accounts can enter.</p>
            </div>
          )}
          {googleClientId && <div className="my-5 flex items-center gap-3 text-xs text-slate-400"><span className="h-px bg-slate-200 flex-1" /><span>Email and password</span><span className="h-px bg-slate-200 flex-1" /></div>}
          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required data-testid="login-email-input" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required data-testid="login-password-input" className="mt-1" />
            </div>
            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading} data-testid="login-submit-button">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Sign in
            </Button>
          </div>
        </form>
      </div>
      <div className="hidden lg:block relative">
        <img src={BG} alt="architecture" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/65 via-slate-900/45 to-blue-950/65" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="text-3xl font-display font-bold tracking-tight">One system for the whole company.</div>
          <div className="text-sm text-slate-200 mt-3 max-w-md leading-relaxed">Less chasing, less repeating, less hassle. More clarity, more progress — growing together, one step at a time.</div>
        </div>
      </div>
    </div>
  );
}
